// Product variants (colour / size) on the web.
//
// Product shape (API_CONTRACT §2, utils/catalog.py PUBLIC_PRODUCT_KEYS):
//   colors: ["Black", "Cognac"]      sizes: ["S", "M", "L"] | ["One Size"] | []
//   stock:  one number for the whole product (there is no per-size stock yet).
//
// Sizes are read tolerantly: a plain string, or an object such as
// {size|label|name|value, stock|available|in_stock}. Today the backend only
// sends strings, so every size is available while the product has stock. An
// object that says it has no stock is shown struck through and disabled.
//
// Cart lines are per variant (API_CONTRACT §3, G16): the server keeps one
// line per (product, size, colour) and returns `id`, `size`, `color` and
// `variant_id` ("<colour>|<size>") on each GET /cart line. POST /cart/add
// takes `size` / `color` (and still reads `variant_id`). The guest bag keeps
// the same "<colour>|<size>" `variant_id` per line; orders take `size` /
// `color` per line (models/order.py).

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

const sizeValue = (entry) => {
  if (entry && typeof entry === 'object') {
    return text(entry.size ?? entry.value ?? entry.label ?? entry.name);
  }
  return text(entry);
};

const sizeAvailable = (entry) => {
  if (!entry || typeof entry !== 'object') return true;
  if (entry.available === false || entry.in_stock === false) return false;
  if (entry.stock !== undefined && entry.stock !== null) {
    const n = Number(entry.stock);
    if (Number.isFinite(n)) return n > 0;
  }
  return true;
};

const sizeStock = (entry) => {
  if (!entry || typeof entry !== 'object' || entry.stock === undefined || entry.stock === null) return null;
  const n = Number(entry.stock);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
};

/** [{value, available, stock|null}] for the product's sizes (empty = no size selector). */
export const productSizes = (product) => {
  const raw = Array.isArray(product?.sizes) ? product.sizes : [];
  const seen = new Set();
  const out = [];
  raw.forEach((entry) => {
    const value = sizeValue(entry);
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push({ value, available: sizeAvailable(entry), stock: sizeStock(entry) });
  });
  return out;
};

/** Colour names (strings or {name}) without blanks or duplicates. */
export const productColors = (product) => {
  const raw = Array.isArray(product?.colors) ? product.colors : [];
  const seen = new Set();
  return raw
    .map((entry) => text(entry && typeof entry === 'object' ? entry.name ?? entry.label : entry))
    .filter((value) => value && !seen.has(value) && seen.add(value));
};

/** Whole-product stock as a non-negative integer. */
export const productStock = (product) => {
  const n = Number(product?.stock);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
};

/** Most units the shopper may add: product stock, or the chosen size's stock when known. */
export const maxQuantity = (product, size) => {
  const stock = productStock(product);
  const chosen = productSizes(product).find((entry) => entry.value === size);
  if (chosen && chosen.stock !== null) return Math.min(stock, chosen.stock);
  return stock;
};

/** "<colour>|<size>" for POST /cart/add `variant_id`; null when nothing was chosen. */
export const encodeVariant = ({ color, size } = {}) => {
  const c = text(color);
  const s = text(size);
  if (!c && !s) return null;
  return `${c}|${s}`;
};

/**
 * {color, size} from a cart line's `variant_id`. Older bags stored a single
 * value without "|" (colour or size, unknown which): returned as `label` only.
 */
export const decodeVariant = (variantId) => {
  const raw = text(variantId);
  if (!raw) return { color: null, size: null, label: '' };
  if (!raw.includes('|')) return { color: null, size: null, label: raw };
  const [c, ...rest] = raw.split('|');
  const color = text(c) || null;
  const size = text(rest.join('|')) || null;
  return { color, size, label: [color, size].filter(Boolean).join(' · ') };
};

/** `size` / `color` for an order line (POST /orders items[]), from the line or its variant_id. */
export const orderLineVariant = (item) => {
  const decoded = decodeVariant(item?.variant_id);
  const size = text(item?.size) || decoded.size;
  const color = text(item?.color) || decoded.color;
  return {
    ...(size ? { size } : {}),
    ...(color ? { color } : {}),
  };
};

/** {size, color, label} of a bag line: the server's `size`/`color`, else its `variant_id`. */
export const lineVariant = (item) => {
  const decoded = decodeVariant(item?.variant_id);
  const size = text(item?.size) || decoded.size || null;
  const color = text(item?.color) || decoded.color || null;
  const label = size || color ? [color, size].filter(Boolean).join(' · ') : decoded.label;
  return { size, color, label };
};

/** Normalised "<colour>|<size>" of a line (case-insensitive), for matching guest lines. */
const variantKey = (item) => {
  const { size, color, label } = lineVariant(item);
  if (!size && !color) return label.toLowerCase();
  return `${(color || '').toLowerCase()}|${(size || '').toLowerCase()}`;
};

/**
 * Stable key of one bag line: the server line `id` when there is one, else
 * product + variant (guest bag). Two sizes of one piece are two keys.
 */
export const cartLineKey = (item) => {
  if (item?.id) return String(item.id);
  const productId = item?.product_id || item?.product?.id || '';
  return `${productId}::${variantKey(item)}`;
};

/** True when a bag line is this product in this variant ("<colour>|<size>"). */
export const isSameLine = (item, productId, variantId) =>
  (item?.product_id || item?.product?.id) === productId &&
  variantKey(item) === variantKey({ variant_id: variantId });
